import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getOnboardingStateAction,
  updateOnboardingStepAction,
  getAddonDetailsAction,
  createNativeSubscriptionAction,
} from "../../actions";

export interface IOnboardingSteps {
  connect_gbp_status: string;
  import_business_status: string;
  connect_socials_status: string;
  invite_team_status: string;
  install_widget_status: string;
  business_subscribed_status: string;
}

export interface IOnboardingData {
  completed: boolean;
  completed_count: number;
  total_count: number;
  last_step: string | null;
  businesses_added: any[];
  selected_business_id: string | null;
  steps: IOnboardingSteps;
}

export interface IAddonDetailsData {
  addon_id: string;
  name: string;
  price: string;
  currency: string;
  trial_days: number;
  require_payment_upfront: boolean;
}

export interface IOnboardingState {
  onboardingState: IOnboardingData | null;
  addonDetails: IAddonDetailsData | null;
  fetchingAddon: boolean;
  creatingSub: boolean;
  loading: boolean;
  updatingStep: boolean;
  error: string | null;
}

const initialState: IOnboardingState = {
  onboardingState: null,
  addonDetails: null,
  fetchingAddon: false,
  creatingSub: false,
  loading: false,
  updatingStep: false,
  error: null,
};

const onboardingSlice = createSlice({
  name: "onboarding",
  initialState,
  reducers: {
    resetOnboardingState: (state) => {
      state.onboardingState = null;
      state.addonDetails = null;
      state.fetchingAddon = false;
      state.creatingSub = false;
      state.loading = false;
      state.updatingStep = false;
      state.error = null;
      localStorage.removeItem("selected_business");
      localStorage.removeItem("selected_business_id");
      localStorage.removeItem("selecetd");
    },
    setOnboardingData: (state, action: PayloadAction<IOnboardingData>) => {
      state.onboardingState = action.payload;
      // Only set localStorage if user hasn't already selected a business manually.
      if (action.payload?.selected_business_id && !localStorage.getItem("selected_business")) {
        localStorage.setItem("selected_business", action.payload.selected_business_id);
        localStorage.setItem("selected_business_id", action.payload.selected_business_id);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // getOnboardingStateAction
      .addCase(getOnboardingStateAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOnboardingStateAction.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        if (action.payload?.data) {
          state.onboardingState = action.payload.data;
          // Only set localStorage if user hasn't already selected a business manually.
          // This prevents the onboarding default from overwriting the user's saved selection
          // every time this action is dispatched (e.g. on page mount).
          if (action.payload.data.selected_business_id && !localStorage.getItem("selected_business")) {
            localStorage.setItem("selected_business", action.payload.data.selected_business_id);
            localStorage.setItem("selected_business_id", action.payload.data.selected_business_id);
          }
        }
      })
      .addCase(getOnboardingStateAction.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch onboarding state";
      })
      // updateOnboardingStepAction
      .addCase(updateOnboardingStepAction.pending, (state) => {
        state.updatingStep = true;
        state.error = null;
      })
      .addCase(updateOnboardingStepAction.fulfilled, (state) => {
        state.updatingStep = false;
      })
      .addCase(updateOnboardingStepAction.rejected, (state, action: PayloadAction<any>) => {
        state.updatingStep = false;
        state.error = action.payload?.message || "Failed to update onboarding step";
      })
      // getAddonDetailsAction
      .addCase(getAddonDetailsAction.pending, (state) => {
        state.fetchingAddon = true;
      })
      .addCase(getAddonDetailsAction.fulfilled, (state, action: PayloadAction<any>) => {
        state.fetchingAddon = false;
        state.addonDetails = action.payload?.data || action.payload || null;
      })
      .addCase(getAddonDetailsAction.rejected, (state) => {
        state.fetchingAddon = false;
      })
      // createNativeSubscriptionAction
      .addCase(createNativeSubscriptionAction.pending, (state) => {
        state.creatingSub = true;
      })
      .addCase(createNativeSubscriptionAction.fulfilled, (state) => {
        state.creatingSub = false;
      })
      .addCase(createNativeSubscriptionAction.rejected, (state) => {
        state.creatingSub = false;
      });
  },
});

export const { resetOnboardingState, setOnboardingData } = onboardingSlice.actions;
export default onboardingSlice.reducer;
